import { computed, ref, watchEffect } from 'vue'
import { useHead } from '@vueuse/head'
import { useRoute } from 'vue-router'

const INDEXABLE = 'index, follow'
// noarchive also keeps these URLs out of caches such as the Internet Archive
const PRIVATE = 'noindex, nofollow, noarchive'

const siteUrl = () =>
  process.env.VUE_APP_SITE_URL || window.location.origin

const pendingContentPath = ref<string | null>(null)

/**
 * Holds `noindex` on `path` until its content has actually arrived, so a
 * spinner or an error page is never indexed as if it were real content.
 * Keyed by path, so a stale mark from an earlier navigation is ignored.
 */
export const markContentPending = (path: string) => {
  pendingContentPath.value = path
}

export const markContentReady = () => {
  pendingContentPath.value = null
}

/**
 * Reactive form of the above for views that expose their content as refs:
 * the current path stays `noindex` for as long as `isReady` returns false.
 */
export const useContentIndexing = (isReady: () => boolean) => {
  const route = useRoute()

  watchEffect(() => {
    if (isReady()) markContentReady()
    else markContentPending(route.path)
  })
}

/**
 * Emits a `robots` meta tag for every route, plus a self referencing
 * `canonical` link on the public ones.
 *
 * Indexing is opt-in: a route is only indexable when it sets `meta.indexable`
 * to true, so any new route (booking flows, account pages, tokenised links)
 * is `noindex` by default and cannot leak into search engines.
 */
export default function useRobots () {
  const route = useRoute()
  const indexingEnabled = process.env.VUE_APP_META_INDEXING_ENABLED !== '0'

  const isIndexable = computed(() =>
    indexingEnabled &&
    route.meta.indexable === true &&
    pendingContentPath.value !== route.path
  )

  useHead({
    meta: [
      {
        name: 'robots',
        content: computed(() => isIndexable.value ? INDEXABLE : PRIVATE),
        key: 'robots'
      }
    ],
    link: computed(() =>
      isIndexable.value
        ? [{
            rel: 'canonical',
            href: new URL(route.path, siteUrl()).href,
            key: 'canonical'
          }]
        : []
    )
  })
}
